/**
 * Review & publish — the enterprise VPAT production line.
 *
 *   1. Pick a completed scan.
 *   2. Work the review queue: confirm or skip every automated finding
 *      (overrides happen on the Scans page and count as reviewed).
 *   3. Approve on the VPAT approvals page (hash attestation).
 *   4. Publish — freezes an immutable version clients can be linked to.
 */

import { useEffect, useState } from 'preact/hooks';
import { DashboardApi } from '../api.js';
import type { AuthOrg } from '../auth.js';
import { Banner } from '../components/Banner.js';

const api = new DashboardApi();

interface QueueRow {
  criterion: string;
  conformance: string;
  remarks: string;
  confidence: number | null;
  reviewState: 'pending' | 'confirmed' | 'skipped' | 'overridden' | 'auto-confirmed';
  override?: { conformance: string; reason: string };
  reviewedAt?: string;
  note?: string | null;
}

interface Progress {
  total: number;
  reviewed: number;
  remaining: string[];
  complete: boolean;
}

interface PublishedVersion {
  id: string;
  version: number;
  edition: string;
  standards: string[];
  contentSha256: string;
  publishedAt: string;
}

const STATE_PILL: Record<QueueRow['reviewState'], string> = {
  pending: 'pill-partial',
  confirmed: 'pill-supports',
  skipped: 'pill-muted',
  overridden: 'pill-info',
  'auto-confirmed': 'pill-supports',
};

export function Review({ org }: { org: AuthOrg }): preact.ComponentChildren {
  const [scans, setScans] = useState<
    Array<{ id: string; productName: string; productVersion: string; status: string | null }>
  >([]);
  const [scanId, setScanId] = useState<string>('');
  const [queue, setQueue] = useState<QueueRow[] | null>(null);
  const [progress, setProgress] = useState<Progress | null>(null);
  const [versions, setVersions] = useState<PublishedVersion[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busyCriterion, setBusyCriterion] = useState<string | null>(null);

  // Publish form
  const [vendorName, setVendorName] = useState('');
  const [contactInfo, setContactInfo] = useState('');
  const [productDescription, setProductDescription] = useState('');
  const [edition, setEdition] = useState('INT');
  const [publishing, setPublishing] = useState(false);
  const [publishResult, setPublishResult] = useState<string | null>(null);
  // Auto-confirm policy: criteria whose every finding row has confidence >=
  // threshold count as reviewed. Empty = fully manual review.
  const [autoConfirm, setAutoConfirm] = useState<string>('');

  useEffect(() => {
    api
      .listScans(org.id)
      .then((r) => setScans(r.scans.filter((s) => s.status === 'completed')))
      .catch((e) => setError(String(e)));
  }, [org.id]);

  const loadScan = async (id: string, thresholdOverride?: string) => {
    setScanId(id);
    setQueue(null);
    setPublishResult(null);
    if (!id) return;
    const t = thresholdOverride ?? autoConfirm;
    const threshold = t !== '' ? Number.parseFloat(t) : undefined;
    try {
      const [r, v] = await Promise.all([
        api.reviewQueue(org.id, id, threshold),
        api.publishedVersions(org.id, id),
      ]);
      setQueue(r.queue);
      setProgress(r.progress);
      setVersions(v.versions);
      setError(null);
    } catch (e) {
      setError(String(e));
    }
  };

  const decide = async (criterion: string, action: 'confirmed' | 'skipped' | 'clear') => {
    setBusyCriterion(criterion);
    try {
      const r = await api.reviewFinding(org.id, scanId, { criterion, action });
      setProgress(r.progress);
      setQueue(
        (q) =>
          q?.map((row) =>
            row.criterion === criterion
              ? {
                  ...row,
                  reviewState: action === 'clear' ? 'pending' : action,
                  reviewedAt: new Date().toISOString(),
                }
              : row,
          ) ?? null,
      );
    } catch (e) {
      setError(String(e));
    } finally {
      setBusyCriterion(null);
    }
  };

  const publish = async (e: Event) => {
    e.preventDefault();
    setPublishing(true);
    setPublishResult(null);
    try {
      const threshold = autoConfirm !== '' ? Number.parseFloat(autoConfirm) : undefined;
      const r = await api.publishVpat(org.id, scanId, {
        edition,
        standards: ['WCAG 2.1 Level A', 'WCAG 2.1 Level AA'],
        vendorName,
        contactInfo,
        productDescription,
        ...(threshold !== undefined ? { autoConfirmMinConfidence: threshold } : {}),
      });
      setPublishResult(
        `Published v${r.published.version} · sha256 ${r.published.contentSha256.slice(0, 12)}…`,
      );
      const v = await api.publishedVersions(org.id, scanId);
      setVersions(v.versions);
      setError(null);
    } catch (e) {
      setError(String(e));
    } finally {
      setPublishing(false);
    }
  };

  const pct = progress ? Math.round((progress.reviewed / Math.max(progress.total, 1)) * 100) : 0;

  return (
    <div>
      <div class="page-header">
        <h1>Review &amp; publish</h1>
        <p class="muted">
          Confirm every automated finding, then freeze an immutable VPAT version for clients.
          Overrides (made on the Scans page) count as reviewed. Publishing requires an active
          approval from the VPAT approvals page.
        </p>
      </div>

      {error && <Banner variant="warn">{error}</Banner>}

      <div class="card" style={{ marginBottom: 16 }}>
        <label htmlFor="review-scan">Completed scan</label>
        <select
          id="review-scan"
          value={scanId}
          onChange={(e) => void loadScan((e.target as HTMLSelectElement).value)}
        >
          <option value="">Select a scan…</option>
          {scans.map((s) => (
            <option key={s.id} value={s.id}>
              {s.productName} {s.productVersion} — {s.id.slice(0, 8)}
            </option>
          ))}
        </select>
      </div>

      {progress && (
        <div class="card" style={{ marginBottom: 16 }}>
          <h2>
            Review progress: {progress.reviewed} / {progress.total}
            {progress.complete ? ' · ready to approve + publish' : ''}
          </h2>
          <progress
            value={pct}
            max={100}
            aria-label="Criteria reviewed"
            style={{ width: '100%', height: 10 }}
          />
          <div class="field" style={{ marginTop: 12, maxWidth: 380 }}>
            <label htmlFor="auto-confirm">Auto-confirm threshold (0–1, blank = fully manual)</label>
            <input
              id="auto-confirm"
              type="number"
              min="0"
              max="1"
              step="0.05"
              placeholder="e.g. 0.9"
              value={autoConfirm}
              onChange={(e) => {
                const v = (e.target as HTMLInputElement).value;
                setAutoConfirm(v);
                void loadScan(scanId, v);
              }}
            />
            <p class="muted small" style={{ marginTop: 4 }}>
              Criteria where every finding has probe-backed confidence at or above the threshold are
              treated as reviewed. The policy is recorded in the audit log when you publish.
            </p>
          </div>
        </div>
      )}

      {queue && (
        <div class="card" style={{ marginBottom: 16 }}>
          <h2>Queue (unreviewed + lowest confidence first)</h2>
          <table class="audit">
            <thead>
              <tr>
                <th>Criterion</th>
                <th>Verdict</th>
                <th>Remarks</th>
                <th>Conf.</th>
                <th>State</th>
                <th>{/* actions */}</th>
              </tr>
            </thead>
            <tbody>
              {queue.map((row) => (
                <tr key={row.criterion}>
                  <td>
                    <code>{row.criterion}</code>
                  </td>
                  <td>
                    <span class="kind-pill">{row.override?.conformance ?? row.conformance}</span>
                    {row.override && <div class="small muted">overridden</div>}
                  </td>
                  <td class="small" style={{ maxWidth: 420 }}>
                    {row.remarks.slice(0, 200)}
                  </td>
                  <td>{row.confidence === null ? '—' : row.confidence.toFixed(2)}</td>
                  <td>
                    <span class={`kind-pill ${STATE_PILL[row.reviewState]}`}>
                      {row.reviewState}
                    </span>
                  </td>
                  <td>
                    {row.reviewState === 'pending' || row.reviewState === 'skipped' ? (
                      <button
                        type="button"
                        disabled={busyCriterion === row.criterion}
                        onClick={() => void decide(row.criterion, 'confirmed')}
                        style={{ marginRight: 6 }}
                      >
                        Confirm
                      </button>
                    ) : null}
                    {row.reviewState === 'pending' ? (
                      <button
                        type="button"
                        class="secondary"
                        disabled={busyCriterion === row.criterion}
                        onClick={() => void decide(row.criterion, 'skipped')}
                      >
                        Skip
                      </button>
                    ) : null}
                    {row.reviewState === 'confirmed' ? (
                      <button
                        type="button"
                        class="secondary"
                        disabled={busyCriterion === row.criterion}
                        onClick={() => void decide(row.criterion, 'clear')}
                      >
                        Undo
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {progress?.complete && (
        <div class="card" style={{ marginBottom: 16 }}>
          <h2>Publish immutable version</h2>
          <p class="muted small">
            Requires an active approval (VPAT approvals page) matching the current findings. The
            snapshot is frozen — share links pinned to it never change.
          </p>
          <form onSubmit={publish}>
            <div class="field">
              <label htmlFor="pub-vendor">Vendor name</label>
              <input
                id="pub-vendor"
                required
                value={vendorName}
                onInput={(e) => setVendorName((e.target as HTMLInputElement).value)}
              />
            </div>
            <div class="field">
              <label htmlFor="pub-contact">Contact info</label>
              <input
                id="pub-contact"
                required
                value={contactInfo}
                onInput={(e) => setContactInfo((e.target as HTMLInputElement).value)}
              />
            </div>
            <div class="field">
              <label htmlFor="pub-desc">Product description</label>
              <input
                id="pub-desc"
                required
                value={productDescription}
                onInput={(e) => setProductDescription((e.target as HTMLInputElement).value)}
              />
            </div>
            <div class="field">
              <label htmlFor="pub-edition">Edition</label>
              <select
                id="pub-edition"
                value={edition}
                onChange={(e) => setEdition((e.target as HTMLSelectElement).value)}
              >
                <option value="INT">INT — International</option>
                <option value="WCAG">WCAG only</option>
                <option value="508">Revised Section 508</option>
                <option value="EU">EU (EN 301 549)</option>
              </select>
            </div>
            <button type="submit" disabled={publishing}>
              {publishing ? 'Publishing…' : 'Publish version'}
            </button>
            {publishResult && (
              <span class="small" style={{ marginLeft: 12, color: 'var(--success, #4ade80)' }}>
                {publishResult}
              </span>
            )}
          </form>
        </div>
      )}

      {versions.length > 0 && (
        <div class="card">
          <h2>Published versions</h2>
          <table class="audit">
            <thead>
              <tr>
                <th>Version</th>
                <th>Edition</th>
                <th>Published</th>
                <th>Content SHA-256</th>
              </tr>
            </thead>
            <tbody>
              {versions.map((v) => (
                <tr key={v.id}>
                  <td>v{v.version}</td>
                  <td>{v.edition}</td>
                  <td class="small">{new Date(v.publishedAt).toLocaleString()}</td>
                  <td>
                    <code class="small">{v.contentSha256.slice(0, 16)}…</code>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p class="muted small">
            Pin a share link to a version from the Compliance page to give clients a frozen
            document.
          </p>
        </div>
      )}
    </div>
  );
}
